import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import { catchError, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as MerchantActions from '../action/auth.actions';
import { RegisterUserService } from 'src/app/services/registerUser/register-user.service';
import {
  selectMerchantDetails,
  selectReserveFund,
  selectMerchantKeyValidation,
  selectQRlink,
  selectPermissions,
  selectTransactionDetailsCount,
  selectEsignProcessStatus,
  selectPaymentLink,
  selectLatestMerchantSalesContact
} from '../selectors/auth.selectors';

@Injectable()
export class MerchantEffects {

  loadMerchantDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.loadMerchantDetails),
      withLatestFrom(this.store.select(selectMerchantDetails)),
      mergeMap(([action, merchantDetails]) =>
        merchantDetails ? of() : this.registerService.GetMerchantOnboardStatus().pipe(
          tap(response => console.log('Load Merchant Details Response:', response)),
          map(response => MerchantActions.loadMerchantDetailsSuccess({ merchant: response.Data })),
          catchError(error => {
            console.error('Load Merchant Details Error:', error);
            return of(MerchantActions.loadMerchantDetailsFailure({ error }));
          })
        )
      )
    )
  );
  getQRCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.getQRCode),
      withLatestFrom(this.store.select(selectQRlink)),
      mergeMap(([action, QRlink]) =>
        QRlink ? of() : this.registerService.getQrcode(action.merchantKeyId).pipe(
          tap(response => console.log('Get QR Code Response:', response)),
          map(response => MerchantActions.getQRCodeSuccess({ QRlink: response.UpiLink })),
          catchError(error => {
            console.error('Get QR Code Error:', error);
            return of(MerchantActions.getQRCodeFailure({ error }));
          })
        )
      )
    )
  );
  loadReserveFund$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.loadReserveFund),
      withLatestFrom(this.store.select(selectReserveFund)),
      mergeMap(([action, reserveFund]) =>
        reserveFund !== null ? of() : this.registerService.GetReserveFund(action.merchantKeyId).pipe(
          tap(response => console.log('Load Reserve Fund Response:', response)),
          map(response => MerchantActions.loadReserveFundSuccess({ reserveFund: response.length > 0 })),
          catchError(error => {
            console.error('Load Reserve Fund Error:', error);
            return of(MerchantActions.loadReserveFundFailure({ error }));
          })
        )
      )
    )
  );

  checkMerchantKeyId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.checkMerchantKeyId),
      withLatestFrom(this.store.select(selectMerchantKeyValidation)),
      mergeMap(([action, merchantKeyValidation]) =>
        merchantKeyValidation !== null ? of() : this.registerService.isValidMerchantKeyId(action.merchantKeyId, action.resellerId).pipe(
          tap(result => console.log('Check Merchant Key ID Response:', result)),
          map(result => MerchantActions.checkMerchantKeyIdSuccess({ success: result.Status })),
          catchError(error => {
            console.error('Check Merchant Key ID Error:', error);
            return of(MerchantActions.checkMerchantKeyIdFailure({ error }));
          })
        )
      )
    )
  );

  
  
  
  

  getUserPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.getUserPermissions),
      withLatestFrom(this.store.select(selectPermissions)),
      mergeMap(([action, permissions]) =>
        permissions ? of() : this.registerService.GetuserRoleBasedPermissions(action.userId, action.appName).pipe(
          tap(response => console.log('Get User Permissions Response:', response)),
          map(response => MerchantActions.getUserPermissionsSuccess({ permissions: response })),
          catchError(error => {
            console.error('Get User Permissions Error:', error);
            return of(MerchantActions.getUserPermissionsFailure({ error }));
          })
        )
      )
    )
  );

  loadTransactionDetailsCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.loadTransactionDetailsCount),
      withLatestFrom(this.store.select(selectTransactionDetailsCount)),
      mergeMap(([action, transactionDetailsCount]) =>
        transactionDetailsCount ? of() : this.registerService.transactionDetailscount(action.fromDate, action.toDate).pipe(
          tap(response => console.log('Load Transaction Details Count Response:', response)),
          map(response => MerchantActions.loadTransactionDetailsCountSuccess({ response })),
          catchError(error => {
            console.error('Load Transaction Details Count Error:', error);
            return of(MerchantActions.loadTransactionDetailsCountFailure({ error }));
          })
        )
      )
    )
  );

  loadEsignProcessStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.loadEsignProcessStatus),
      withLatestFrom(this.store.select(selectEsignProcessStatus)),
      mergeMap(([action, esignProcessStatus]) =>
        esignProcessStatus ? of() : this.registerService.getEsignProcessStatus().pipe(
          tap(response => console.log('Load Esign Process Status Response:', response)),
          map(response => MerchantActions.loadEsignProcessStatusSuccess({ response })),
          catchError(error => {
            console.error('Load Esign Process Status Error:', error);
            return of(MerchantActions.loadEsignProcessStatusFailure({ error }));
          })
        )
      )
    )
  );

  loadPaymentLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.loadPaymentLink),
      withLatestFrom(this.store.select(selectPaymentLink)),
      mergeMap(([action, paymentLink]) =>
        paymentLink ? of() : this.registerService.getDashBoardQuickPayLink().pipe(
          tap(response => console.log('Load Payment Link Response:', response)),
          map(response => MerchantActions.loadPaymentLinkSuccess({ response })),
          catchError(error => {
            console.error('Load Payment Link Error:', error);
            return of(MerchantActions.loadPaymentLinkFailure({ error }));
          })
        )
      )
    )
  );

  loadLatestMerchantSalesContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.loadLatestMerchantSalesContact),
      withLatestFrom(this.store.select(selectLatestMerchantSalesContact)),
      mergeMap(([action, latestMerchantSalesContact]) =>
        latestMerchantSalesContact ? of() : this.registerService.getLatestMerchantSalesContact().pipe(
          tap(response => console.log('Load Latest Merchant Sales Contact Response:', response)),
          map(response => MerchantActions.loadLatestMerchantSalesContactSuccess({ response })),
          catchError(error => {
            console.error('Load Latest Merchant Sales Contact Error:', error);
            return of(MerchantActions.loadLatestMerchantSalesContactFailure({ error }));
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private registerService: RegisterUserService,
    private store: Store
  ) { }
}
